import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';

import ReactSlider from 'react-slider';
import Select from 'react-select';
import Popup from 'reactjs-popup';
import type { EventType, PopupPosition } from 'reactjs-popup/dist/types';
import { useMediaQuery } from 'react-responsive';
import Modal from 'react-bootstrap/Modal';

import { SessionState } from 'features/session/sessionSlice';
import { useAppSelector } from 'app/hooks';

import { TUITION_RANGES, MONTHS } from 'common/constants';
import {
  useLazyGetIntakeMonthsQuery,
  useLazyGetLocationsForFilterQuery,
  useLazyGetSubjectsForFilterQuery,
  useLazyGetSchoolsForFilterQuery,
} from 'services/exploreApi';

import styles from './Explore.module.scss';
import { useNavigate } from 'react-router-dom';
const splitRange = (fallback: number[], value?: string) => {
  if (!value) return fallback;
  const splited = value.split('|');
  return [Number(splited[0]), Number(splited[1])];
};

const monthsOptions = [
  { label: 'All year', value: undefined },
  ...MONTHS.map((v: string, i: number) => ({ label: v, value: i + 1 })),
];

const degreeTypeOptions = [
  { label: 'Bachelors', value: 'bachelors' },
  { label: 'Masters', value: 'masters' },
  // { label: 'Integrated', value: 2 },
  // { label: 'Diploma', value: 3 },
];

const popupProps = {
  position: ['bottom left'] as PopupPosition[],
  offsetY: 14,
  on: ['click'] as EventType[],
  arrow: false,
  keepTooltipInside: 'body',
};

const popupTrigger = (
  label: string,
  active = false,
  display = false,
  size: string,
  amount = 0,
  degree = false
) => {
  const className = `${
    active ? 'active' : undefined
  } btn btn--filter rounded-pill w-auto px-4 py-2 shadow-sm mx-2 ${
    display ? 'd-none' : undefined
  } ${size} ${degree ? 'degree-type-blue-btn' : 'bg-white'}`;
  return (
    <button className={className}>
      {label}
      {amount > 0 && (
        <span className="ms-2 badge rounded-pill bg-primary text-white">
          <small>{amount}</small>
        </span>
      )}
      <i className="ms-2 bi bi-chevron-down"></i>
    </button>
  );
};

interface CompProps {
  onFilterChange: (key: string, value: any) => void;
  onAppliedChange: (key: string, value: any) => void;
  setShowFilters: React.Dispatch<React.SetStateAction<boolean>>;
  applyFilters: () => void;
  clearFilters: () => void;
  showFilters: boolean;
  appliedFilters: { [key: string]: any };
  searchFilters: { [key: string]: any };
  searchParams: { [key: string]: any };
  degreeSelected: string;
}

const Filters = ({
  onFilterChange,
  onAppliedChange,
  setShowFilters,
  applyFilters,
  clearFilters,
  showFilters,
  appliedFilters,
  searchFilters,
  degreeSelected,
}: CompProps): React.ReactElement => {
  const [prevScrollPos, setPrevScrollPos] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const navigate = useNavigate();

  const [
    getLocations,
    { data: { data: locations } = { data: [] } as { data: any[] }, isLoading: locationsLoading },
  ] = useLazyGetLocationsForFilterQuery();

  const locationParams = {
    query: '',
    cities: searchFilters.city_id,
    countries: searchFilters.country_id,
  };

  const [
    getSubjects,
    { data: { data: subjects } = { data: [] } as { data: any[] }, isLoading: subjectsLoading },
  ] = useLazyGetSubjectsForFilterQuery();

  const subjectParams = { query: '', ids: searchFilters.subject_id };

  const [
    getSchools,
    { data: { data: schools } = { data: [] } as { data: any[] }, isLoading: schoolsLoading },
  ] = useLazyGetSchoolsForFilterQuery();
  const schoolParams = {
    query: '',
    ids: searchFilters.school_id,
  };

  const [getIntakeMonths, { data: { data: intakeMonths } = { data: [] } as { data: any[] } }] =
    useLazyGetIntakeMonthsQuery();
  //  useGetIntakeMonthsQuery();
  const handleCloseFilters = () => setShowFilters(false);

  const filteredMonthOptions = useMemo(() => {
    if (intakeMonths === undefined) return monthsOptions;
    return monthsOptions.filter(
      ({ value }) => value === undefined || intakeMonths?.includes(value)
    );
  }, [intakeMonths]);

  const onMultiSelect =
    (key: string, onChange = onFilterChange) =>
    (selection: { type: string; value: number; label: string }[]) => {
      if (key === 'locations') {
        const countries = selection.map(({ value }) => value);
        onChange('country_id', countries.length ? countries : undefined);
      } else if (key === 'subjects') {
        const subjects = selection.map(({ value }) => value);
        onChange('subject_id', subjects.length ? subjects : undefined);
      } else if (key === 'universities') {
        const universities = selection.map(({ value }) => value);
        onChange('school_id', universities.length ? universities : undefined);
      }
    };

  const onFilterCheckInputChange =
    (key: string, onlyTrue = true) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      let value: boolean | undefined = event.target.checked;
      if (onlyTrue && !event.target.checked) value = undefined;
      onFilterChange(key, value);
    };

  const onFilterRangeInputChange =
    (key: string) =>
    ([min, max]: number[]) =>
      onFilterChange(key, `${min}|${max}`);

  const onFilterSelectInputChange = (key: string) => (value: any) => {
    if (Array.isArray(value))
      onFilterChange(
        key,
        value.map(({ value }: { value: any }) => value)
      );
    else onFilterChange(key, value.value);
  };

  const durationFilter = useCallback(
    (onChange = onFilterChange, filters = searchFilters) => (
      <div className={styles.filtersFormInput}>
        <label>Duration</label>
        <div className={styles.filtersFormButtons}>
          {[
            ['Any', undefined],
            ['12 months', 12],
            ['18 months', 18],
            ['24 months', 24],
          ].map(([k, v]) => (
            <Fragment key={k}>
              <input
                type="radio"
                className="btn-check"
                name="duration"
                id={`duration-${v}`}
                checked={filters.duration === v}
                onChange={() => onChange('duration', v)}
              />
              <label className="btn btn-primary" htmlFor={`duration-${v}`}>
                {k}
              </label>
            </Fragment>
          ))}
        </div>
      </div>
    ),
    [searchFilters, appliedFilters]
  );

  const tuitionFilter = useCallback(
    (onChange = onFilterChange, filters = searchFilters, title = 'Tuition', filter = 'tuition') => (
      <div className={styles.filtersFormInput}>
        <label>
          {title}
          <span>USD</span>
        </label>
        <div className={styles.filtersFormButtons}>
          {TUITION_RANGES.map(([k, min, max]) => (
            <Fragment key={k}>
              <input
                type="radio"
                className="btn-check"
                name={filter}
                id={`${filter}-${min}`}
                checked={filters[`${filter}_min`] === min}
                onChange={() => {
                  onChange(`${filter}_min`, min);
                  onChange(`${filter}_max`, max);
                }}
              />
              <label className="btn btn-primary" htmlFor={`${filter}-${min}`}>
                {k}
              </label>
            </Fragment>
          ))}
        </div>
      </div>
    ),
    [searchFilters, appliedFilters]
  );

  const handleScroll = () => {
    const currentScrollPos = window.scrollY;
    if (currentScrollPos > prevScrollPos) {
      setIsVisible(false);
    } else {
      setIsVisible(true);
    }

    setPrevScrollPos(currentScrollPos);
  };

  const filtersApplied = () => {
    let count = 0;
    Object.keys(appliedFilters).map((k: string) =>
      k.includes('order') || k.includes('query') ? null : (count += 1)
    );
    return count;
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);
  }, [prevScrollPos]);

  return (
    <section className={`${styles.uaSchoolFilters} container-fluid p-relative`}>
      <div className="container">
        <div className="row pt-4 pb-4 p-relative d-flex flex-row align-items-start justify-content-center justify-content-sm-start">
          <Popup
            {...popupProps}
            trigger={popupTrigger(
              `${degreeSelected[0].toUpperCase()}${degreeSelected.substring(1)}'s degree`,
              true,
              true,
              'd-md-block',
              0,
              true
            )}
          >
            <div className={styles.filterPopup}>
              <div className={styles.filtersFormInput}>
                <label>Degree Options</label>
                <Select
                  isClearable
                  classNamePrefix="react-select"
                  className={styles.filtersFormSelect}
                  placeholder="Select Degree Type"
                  options={degreeTypeOptions}
                  onChange={(o) => navigate(`/explore/${o?.value}`)}
                  value={degreeTypeOptions?.filter(
                    ({ value }: { value: string }) => value == degreeSelected
                  )}
                />
              </div>
            </div>
          </Popup>
          <Popup
            {...popupProps}
            trigger={popupTrigger(
              'Locations',
              appliedFilters.city_id || appliedFilters.country_id,
              true,
              'd-md-block',
              appliedFilters.country_id?.length,
              false
            )}
            onOpen={() => locations.length === 0 && getLocations(locationParams)}
          >
            <div className={styles.filterPopup}>
              <div className={styles.filtersFormInput}>
                <label>Location</label>
                <Select
                  isClearable
                  isSearchable
                  isMulti
                  classNamePrefix="react-select"
                  className={styles.filtersFormSelect}
                  placeholder="Search country or city"
                  options={locations}
                  onChange={onMultiSelect('locations', onAppliedChange) as (a: any) => void}
                  isLoading={locationsLoading}
                  value={locations?.filter(({ value }: { value: number }) =>
                    appliedFilters.country_id?.includes(value)
                  )}
                />
              </div>
            </div>
          </Popup>
          <Popup
            {...popupProps}
            trigger={popupTrigger(
              'Subjects',
              appliedFilters.subject_id,
              true,
              'd-lg-block',
              appliedFilters.subject_id?.length
            )}
            onOpen={() => subjects.length === 0 && getSubjects(subjectParams)}
          >
            <div className={styles.filterPopup}>
              <div className={styles.filtersFormInput}>
                <label>Subjects</label>
                <Select
                  isClearable
                  isSearchable
                  isMulti
                  hideSelectedOptions={false}
                  classNamePrefix="react-select"
                  className={styles.filtersFormSelect}
                  placeholder="Search for subjects"
                  options={subjects}
                  onChange={onMultiSelect('subjects', onAppliedChange) as (a: any) => void}
                  isLoading={subjectsLoading}
                  value={subjects?.filter(({ value }: { value: number }) =>
                    appliedFilters.subject_id?.includes(value)
                  )}
                />
              </div>
            </div>
          </Popup>
          <Popup
            {...popupProps}
            trigger={popupTrigger(
              'University Name',
              appliedFilters.school_id,
              true,
              'd-lg-block',
              appliedFilters.school_id?.length
            )}
            onOpen={() => schools.length === 0 && getSchools(schoolParams)}
          >
            <div className={styles.filterPopup}>
              <div className={styles.filtersFormInput}>
                <label>University</label>
                <Select
                  isClearable
                  isSearchable
                  isMulti
                  hideSelectedOptions={false}
                  classNamePrefix="react-select"
                  className={styles.filtersFormSelect}
                  placeholder="Search for universities"
                  options={schools}
                  onChange={onMultiSelect('universities', onAppliedChange) as (a: any) => void}
                  isLoading={schoolsLoading}
                  value={schools?.filter(({ value }: { value: number }) =>
                    appliedFilters.school_id?.includes(value)
                  )}
                />
              </div>
            </div>
          </Popup>
          <button
            className={`btn btn--filter rounded-pill bg-white w-auto px-4 py-2 shadow-sm mx-2 ${
              filtersApplied() ? 'active' : ''
            }`}
            onClick={() => setShowFilters((o: boolean) => !o)}
          >
            <i
              className={`me-2 bi ${
                filtersApplied() ? 'bi-filter-square-fill text-primary' : 'bi-filter-square'
              }`}
            />
            All Filters
          </button>
          <button
            className={`btn ${
              filtersApplied() ? 'text-primary' : ''
            } ms-auto w-auto rounded-pill d-none d-sm-block`}
            disabled={!filtersApplied()}
            onClick={clearFilters}
          >
            Clear Filters
          </button>
          {showFilters && (
            <Modal
              show={showFilters}
              onHide={handleCloseFilters}
              dialogClassName="animate__animated animate__fadeIn animate__fast"
              contentClassName="position-relative rounded-4 border-0 shadow-sm"
            >
              <Modal.Header className="modal-header position-absolute position-sticky top-0 left-0 w-100 z-9999 border-0">
                <button
                  className="btn btn-light ms-auto ua-school__close-logo"
                  onClick={handleCloseFilters}
                >
                  <i className="fs-4 bi bi-x-lg" />
                </button>
              </Modal.Header>
              <Modal.Body className={styles.filterOpenContainer}>
                <ExploreSort
                  onFilterChange={onFilterChange}
                  searchFilters={searchFilters}
                  onModal={true}
                />
                <div className={styles.filtersForm}>
                  <div className={styles.filtersFormSection}>Filter by</div>
                  <div className={styles.filtersFormInput}>
                    <label>Subjects</label>
                    <Select
                      isClearable
                      isSearchable
                      isMulti
                      hideSelectedOptions={false}
                      classNamePrefix="react-select"
                      className={styles.filtersFormSelect}
                      placeholder="Search for subjects"
                      options={subjects}
                      onChange={onMultiSelect('subjects') as (a: any) => void}
                      isLoading={subjectsLoading}
                      value={subjects?.filter(({ value }: { value: number }) =>
                        searchFilters.subject_id?.includes(value)
                      )}
                    />
                  </div>
                  <div className={styles.filtersFormInput}>
                    <label>Location</label>
                    <Select
                      isClearable
                      isSearchable
                      isMulti
                      classNamePrefix="react-select"
                      className={styles.filtersFormSelect}
                      placeholder="Search country or city"
                      options={locations}
                      onChange={onMultiSelect('locations', onAppliedChange) as (a: any) => void}
                      isLoading={locationsLoading}
                      // value={locations?.filter(({ value, type }: { value: number; type: string }) => {
                      //   if (type === 'country' && appliedFilters.country_id?.includes(value)) return true;
                      //   else if (type === 'city' && appliedFilters.city_id?.includes(value)) return true;
                      //   return false;
                      // })}
                      value={locations?.filter(({ value }: { value: number }) =>
                        appliedFilters.country_id?.includes(value)
                      )}
                    />
                  </div>
                  {durationFilter()}
                  {tuitionFilter()}
                  <div className={styles.filtersFormInput}>
                    <label>Attendance</label>
                    <div className={styles.filtersFormButtons}>
                      {[
                        ['Any', undefined, undefined],
                        ['Full time', 'schedule', 'full_time'],
                        ['Part time', 'schedule', 'part_time'],
                        ['Online', 'delivery', 'online'],
                      ].map(([label, key, v]) => (
                        <Fragment key={label}>
                          <input
                            type="radio"
                            className="btn-check"
                            name="attendance"
                            id={`attendance-${v}`}
                            checked={(() => {
                              if (!key) return !(searchFilters.schedule || searchFilters.delivery);
                              return searchFilters.schedule === v || searchFilters.delivery === v;
                            })()}
                            onChange={() => {
                              onFilterChange('schedule', undefined);
                              onFilterChange('delivery', undefined);
                              if (key) onFilterChange(key, v);
                            }}
                          />
                          <label className="btn btn-primary" htmlFor={`attendance-${v}`}>
                            {label}
                          </label>
                        </Fragment>
                      ))}
                    </div>
                  </div>
                  <div className={styles.filtersFormInput}>
                    <label>Intake Time</label>
                    <div className={`form-check ${styles.filtersFormCheck}`}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={searchFilters.current_year === true}
                        id="current_year"
                        onChange={onFilterCheckInputChange('current_year')}
                      />
                      <label className="form-check-label" htmlFor="actual_year">
                        {'only show programs for intaking this year'}
                      </label>
                    </div>
                    <Select
                      isSearchable
                      classNamePrefix="react-select"
                      className={styles.filtersFormSelect}
                      options={filteredMonthOptions}
                      onChange={onFilterSelectInputChange('intake_date')}
                      value={
                        filteredMonthOptions.find(
                          ({ value }: { value: any }) => searchFilters.intake_date === value
                        ) || []
                      }
                    />
                  </div>
                  <div className={styles.filtersFormInput}>
                    <label>School Rank</label>
                    <div className={styles.filtersFormButtons}>
                      {[
                        ['Any', undefined],
                        ['Top 20', 20],
                        ['Top 50', 50],
                        ['Top 100', 100],
                      ].map(([k, v]) => (
                        <Fragment key={k}>
                          <input
                            type="radio"
                            className="btn-check"
                            name="school_rank"
                            id={`top-${v}`}
                            checked={searchFilters.school_rank === v}
                            onChange={() => onFilterChange('school_rank', v)}
                          />
                          <label className="btn btn-primary" htmlFor={`top-${v}`}>
                            {k}
                          </label>
                        </Fragment>
                      ))}
                    </div>
                  </div>
                  <>
                    <div className={styles.filtersFormInput}>
                      <label>Test Scores</label>
                      <div className={`form-check ${styles.filtersFormCheck}`}>
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={searchFilters.skip_exams === true}
                          id="skip_exams"
                          onChange={onFilterCheckInputChange('skip_exams')}
                        />
                        <label className="form-check-label" htmlFor="skip_exams">
                          {"only show programs that don't require GRE/GMAT"}
                        </label>
                      </div>
                      <div className={styles.filtersFormSliders}>
                        <div className={styles.filtersFormSlider}>
                          <div className={styles.filtersFormSliderTitle}>GRE Score</div>
                          <ReactSlider
                            className={styles.slider}
                            thumbClassName={styles.sliderThumb}
                            trackClassName={styles.sliderTrack}
                            min={200}
                            max={340}
                            step={20}
                            ariaLabel={['Min score', 'Max score']}
                            ariaValuetext={(state) => `Score value ${state.valueNow}`}
                            minDistance={20}
                            onChange={onFilterRangeInputChange('test_score_gre')}
                            value={splitRange([200, 340], searchFilters.test_score_gre)}
                            pearling
                          />
                          <div className={styles.sliderRange}>
                            <span>{splitRange([200, 340], searchFilters.test_score_gre)[0]}</span>
                            <span>{splitRange([200, 340], searchFilters.test_score_gre)[1]}</span>
                          </div>
                        </div>

                        <div className={styles.filtersFormSlider}>
                          <div className={styles.filtersFormSliderTitle}>GMAT Score</div>
                          <ReactSlider
                            className={styles.slider}
                            thumbClassName={styles.sliderThumb}
                            trackClassName={styles.sliderTrack}
                            min={500}
                            max={800}
                            step={50}
                            ariaLabel={['Min score', 'Max score']}
                            ariaValuetext={(state) => `Score value ${state.valueNow}`}
                            minDistance={50}
                            onChange={onFilterRangeInputChange('test_score_gmat')}
                            value={splitRange([500, 800], searchFilters.test_score_gmat)}
                            pearling
                          />
                          <div className={styles.sliderRange}>
                            <span>{splitRange([500, 800], searchFilters.test_score_gmat)[0]}</span>
                            <span>{splitRange([500, 800], searchFilters.test_score_gmat)[1]}</span>
                          </div>
                        </div>

                        <div className={styles.filtersFormSlider}>
                          <div className={styles.filtersFormSliderTitle}>IELTS Score</div>
                          <ReactSlider
                            className={styles.slider}
                            thumbClassName={styles.sliderThumb}
                            trackClassName={styles.sliderTrack}
                            min={0}
                            max={9}
                            step={1}
                            ariaLabel={['Min score', 'Max score']}
                            ariaValuetext={(state) => `Score value ${state.valueNow}`}
                            minDistance={1}
                            onChange={onFilterRangeInputChange('test_score_ielts')}
                            value={splitRange([0, 9], searchFilters.test_score_ielts)}
                            pearling
                          />
                          <div className={styles.sliderRange}>
                            <span>{splitRange([0, 9], searchFilters.test_score_ielts)[0]}</span>
                            <span>{splitRange([0, 9], searchFilters.test_score_ielts)[1]}</span>
                          </div>
                        </div>

                        <div className={styles.filtersFormSlider}>
                          <div className={styles.filtersFormSliderTitle}>TOEFL Score</div>
                          <ReactSlider
                            className={styles.slider}
                            thumbClassName={styles.sliderThumb}
                            trackClassName={styles.sliderTrack}
                            min={0}
                            max={120}
                            step={10}
                            ariaLabel={['Min score', 'Max score']}
                            ariaValuetext={(state) => `Score value ${state.valueNow}`}
                            minDistance={10}
                            onChange={onFilterRangeInputChange('test_score_toefl')}
                            value={splitRange([0, 120], searchFilters.test_score_toefl)}
                            pearling
                          />
                          <div className={styles.sliderRange}>
                            <span>{splitRange([0, 120], searchFilters.test_score_toefl)[0]}</span>
                            <span>{splitRange([0, 120], searchFilters.test_score_toefl)[1]}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                  <div className={styles.filtersFormActions}>
                    <button
                      className="btn btn-primary d-flex justify-content-center"
                      onClick={() => applyFilters()}
                    >
                      Apply filters
                    </button>
                    <button
                      className="btn btn-outline-primary d-flex justify-content-center"
                      onClick={clearFilters}
                    >
                      Clear filters
                    </button>
                  </div>
                </div>
              </Modal.Body>
            </Modal>
          )}
        </div>
      </div>
    </section>
  );
};

export const ExploreSort = ({
  onFilterChange,
  searchFilters,
  onModal,
}: {
  onFilterChange: (key: string, value: any) => void;
  searchFilters: { [key: string]: any };
  onModal: boolean;
}): React.ReactElement => {
  const { profile } = useAppSelector(({ session }: { session: SessionState }) => session);
  const [selectedSort, setSelectedSort] = useState<string>('');

  useEffect(() => {
    if (searchFilters.order_by_tuition_fee) {
      const value = `tuition_${searchFilters.order_by_tuition_fee}`;
      if (selectedSort !== value) setSelectedSort(value);
    } else if (searchFilters.order_by_scoring) {
      const value = 'scoring';
      if (selectedSort !== value) setSelectedSort(value);
    }
  }, [searchFilters.order_by_tuition_fee, searchFilters.order_by_scoring]);

  useEffect(() => {
    if (selectedSort.startsWith('tuition')) {
      onFilterChange('order_by_tuition_fee', selectedSort.endsWith('asc') ? 'asc' : 'desc');
      onFilterChange('order_by_scoring', undefined);
    } else if (selectedSort == 'scoring') {
      onFilterChange('order_by_scoring', 'desc');
      onFilterChange('order_by_tuition_fee', undefined);
    } else {
      onFilterChange('order_by_tuition_fee', undefined);
      onFilterChange('order_by_scoring', undefined);
    }
  }, [selectedSort]);

  return (
    <div
      className={`order-first w-auto order-sm-last ms-sm-auto d-flex ${
        onModal ? 'flex-column' : 'flex-row align-items-center'
      } justify-content-center justify-content-sm-start mb-3 mb-sm-0`}
    >
      <p className={`m-0 fw-semibold text-secondary w-auto me-2 mb-1 ${styles.nowrap}`}>Sort by </p>
      <select
        className="btn rounded-pill w-100 px-4 py-2 btn-outline-light-primary text-dark-primary"
        value={selectedSort}
        onChange={(event) => setSelectedSort(event.target.value)}
      >
        {profile && <option value="scoring">Preferences</option>}
        <option value="">Popularity</option>
        <option value="tuition_asc">Tuition low to high</option>
        <option value="tuition_desc">Tuition high to low</option>
      </select>
    </div>
  );
};

export default Filters;
